import { Component, inject, input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { FormMutationInterface } from "@kernel/form/interfaces/form-mutation.interface";
import { MessageService } from "@kernel/tools/message/service/message.service";
import { Subject, takeUntil } from "rxjs";
import { BillingAccountFormComponent } from "@app/shared/billing-account/components/billing-account-form/billing-account-form.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ISubscription } from "@app/shared/payment/interfaces/subscription.interfaces";
import { CurrencyPipe, DatePipe, NgIf, TitleCasePipe } from "@angular/common";
import { ESubscriptionStatus } from "@app/shared/payment/enums/subscription-status.enum";
import { PopupService } from "@kernel/popups/popup/modules/confirmation-dialog/services/confirmation-popup/popup.service";
import { SubscriptionService } from "@app/shared/payment/services/subscription.service";
import { IBillingAccount } from "@app/shared/billing-account/interfaces/billing-account.interfaces";
import { IPaymentMethod } from "@app/shared/payment-method/interfaces/payment-method.interfaces";
import { listInvoicesTableConfig } from "@app/applications/payment/domains/billing/config/list-invoice-table.config";
import { updateBillingAccountMutationConfig } from "@app/shared/billing-account/config/update-billing-account-mutation.config";
import { PlanFeaturesListComponent } from "@app/applications/payment/domains/purchase/components/plans/plan-features-list/plan-features-list.component";
import { ButtonComponent } from "@kernel/shared/modules/button/components/button/button.component";
import { PaymentMethodsListComponent } from "@app/shared/payment-method/components/payment-methods-list/payment-methods-list.component";
import { TableComponent } from "@kernel/listing/table/components/table/table.component";
import { PaginationComponent } from "@kernel/listing/pagination/components/pagination/pagination.component";
import { BillingAccountFormModel } from "@app/shared/billing-account/models/billing-account-form.model";

@Component({
  selector: "app-billing",
  templateUrl: "./billing.component.html",
  styleUrls: ["./billing.component.scss"],
  imports: [
    TranslatePipe,
    TitleCasePipe,
    DatePipe,
    PlanFeaturesListComponent,
    ButtonComponent,
    PaymentMethodsListComponent,
    TableComponent,
    PaginationComponent,
    NgIf,
    RouterOutlet,
  ],
  providers: [BillingAccountFormModel],
})
export class BillingComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private messageService = inject(MessageService);
  private popupService = inject(PopupService);
  private subscriptionService = inject(SubscriptionService);
  private currencyPipe = inject(CurrencyPipe);

  public myBillingAccount = input<IBillingAccount>();
  public paymentMethods = input<IPaymentMethod[]>([]);

  private destroy$ = new Subject<void>();
  public updateBillingAccountMutationConfig: FormMutationInterface = updateBillingAccountMutationConfig;
  public billingAccountFormModel = new BillingAccountFormModel();
  @ViewChild("billingAccountForm")
  public billingAccountFormComponent!: BillingAccountFormComponent;

  public mySubscription?: ISubscription;
  public listInvoicesTableConfig = listInvoicesTableConfig(this.currencyPipe);

  get canCancelSubscription() {
    return (
      !!this.mySubscription &&
      [ESubscriptionStatus.Active, ESubscriptionStatus.Initiated, ESubscriptionStatus.PastDue].includes(this.mySubscription.status)
    );
  }

  ngOnInit() {
    this.billingAccountFormModel.setEntity({ firstName: "sdf", email: "sdf@sdf.com" });
    this.subscriptionService
      .observeActiveSubscription()
      .pipe(takeUntil(this.destroy$))
      .subscribe(mySubscription => (this.mySubscription = mySubscription ?? void 0));
  }

  handleBillingAccountMutationComplete(_: IBillingAccount) {
    this.messageService.success(
      this.translateService.instant("BILLING_ACCOUNT.UPDATED_SUCCESSFULLY"),
      this.translateService.instant("MESSAGES.DONE")
    );
  }

  openAddPaymentMethod() {
    return this.router.navigate(["/payment/billing", { outlets: { popups: ["payment-method", "add"] } }]);
  }

  openViewPaymentMethod(paymentMethod: IPaymentMethod) {
    return this.router.navigate(["/payment/billing", { outlets: { popups: ["payment-method", "update", paymentMethod.id] } }], {
      state: { paymentMethod },
    });
  }

  async cancelMySubscription() {
    if (!this.mySubscription) {
      return;
    }

    const confirmed = await this.popupService.confirm({
      header: this.translateService.instant("BILLING.CONFIRM_CANCEL_SUBSCRIPTION_MESSAGE"),
      width: "400px",
    });

    if (!confirmed) return;
    this.subscriptionService
      .cancelSubscription({ id: this.mySubscription.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.messageService.success(this.translateService.instant("BILLING.SUBSCRIPTION_CANCELED"), this.translateService.instant("MESSAGES.DONE"));
      });
  }

  async navigateToPlans() {
    await this.router.navigate(["/payment"]);
  }

  ngOnDestroy() {
    // Clean up the subscriptions when the component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }
}
